import { gql, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';
import { ConversationAssigneeGroup } from './conversation-assignee-group';

export type ManageUserConversationAssigneeGroups = {
  userId: string;
  userConversationAssigneeGroups: ConversationAssigneeGroup[];
};

const MANAGE_USER_CONVERSATION_ASSIGNEE_GROUPS_MUTATION = gql`
  mutation ManageUserConversationAssigneeGroups(
    $data: ManageUserConversationAssigneeGroupsDataInput!
  ) {
    manageUserConversationAssigneeGroups(params: $data) {
      userId
      userConversationAssigneeGroups {
        id
        partnerId
        conversationAssigneeGroupId
        icon
        name
      }
    }
  }
`;

export const useManageConversationAssigneeGroups = () => {
  const { showErrorAlert } = useNotifications();

  const [manageUserAssigneeGroupsMutation] = useMutation<{
    manageUserConversationAssigneeGroups: ManageUserConversationAssigneeGroups;
  }>(MANAGE_USER_CONVERSATION_ASSIGNEE_GROUPS_MUTATION, {
    onError: (error) => {
      console.error(error);
      const message =
        "Something went wrong managing user's conversation assignee groups, please try again";
      showErrorAlert(message);
    },
  });

  const manageUserConversationAssigneeGroups = useCallback(
    async (params: ManageUserConversationAssigneeGroups) => {
      if (!params.userId) {
        return;
      }

      const { data } = await manageUserAssigneeGroupsMutation({
        variables: {
          data: {
            userId: params.userId,
            userConversationAssigneeGroups: params.userConversationAssigneeGroups.map((g) => ({
              partnerId: g.partnerId,
              conversationAssigneeId: params.userId,
              conversationAssigneeGroupId: g.conversationAssigneeGroupId,
            })),
          },
        },
        optimisticResponse: {
          manageUserConversationAssigneeGroups: params,
        },
        update: (cache, { data }) => {
          const id = cache.identify({
            __typename: 'PlatformUser',
            id: params.userId,
          });
          const fields = generateFieldModifiersMap({
            conversationAssigneeGroups:
              data?.manageUserConversationAssigneeGroups.userConversationAssigneeGroups,
          });
          cache.modify({
            id,
            fields,
          });
        },
      });

      return data?.manageUserConversationAssigneeGroups;
    },
    [manageUserAssigneeGroupsMutation],
  );

  return {
    manageUserConversationAssigneeGroups,
  };
};
